import {Signal, computed, signal} from "@angular/core";
import {PaymentAuthorisationType, WalletPaymentErrorType, WalletPaymentSubmissionFailure, WalletPaymentSubmissionResult, WalletProvider} from '../payment';
import {PaymentModel} from "../payment.model";
import {ConfigurationModel} from "../../configuration/configuration.model";
import {WalletPaymentProcessor} from "./wallet-payment-processor";

export class StripeWalletPaymentProcessor extends WalletPaymentProcessor {
    public readonly quickPayAvailable: Signal<boolean> = computed<boolean>(() => this.walletProvider() !== undefined);
    public readonly canSubmit: Signal<boolean> = signal<boolean>(true).asReadonly();

    private stripe: any;
    private stripePaymentRequest: any;

    constructor(paymentModel: PaymentModel, configurationModel: ConfigurationModel) {
        super(paymentModel, configurationModel);
    }

    public async init(): Promise<WalletPaymentProcessor> {
        if (!this.paymentModel.paymentPlatformPublicKey()) {
            return undefined;
        }

        // noinspection JSUnresolvedReference
        try {
            this.stripe = new (<any>window).Stripe(this.paymentModel.paymentPlatformPublicKey());
            this.stripePaymentRequest = this.stripe.paymentRequest({
                country: "AU",
                currency: "aud",
                total: {
                    label: "Order total",
                    // payment value will be updated on submission
                    amount: 100
                },
                requestPayerName: this.paymentModel.requestPayerName() || this.paymentModel.requestPayerPostcode(), // Either way, it just requires billing details
                requestPayerEmail: this.paymentModel.requestPayerEmail(),
                requestPayerPhone: this.paymentModel.requestPayerPhone()
            });
        } catch (error) {
            console.error("Stripe wallet payment initialisation failed", error);
            return undefined;
        }

        await this.resolveWalletProvider();
        if (this.walletProvider() == undefined) {
            return undefined;
        }

        this.stripePaymentRequest.on("paymentmethod", (paymentResponse: any) => {
            try {
                paymentResponse.complete("success");
                this.paymentModel.analyticsService.trackWalletPaymentRequestSucceeded(this.walletProvider());
                this.resolveSubmission(new WalletPaymentSubmissionResult(PaymentAuthorisationType.STRIPE_PAYMENT_METHOD, paymentResponse.paymentMethod.id,
                    paymentResponse.payerName, paymentResponse.payerEmail, paymentResponse.payerPhone, paymentResponse.paymentMethod.billing_details.address.postal_code));
            } catch (error) {
                this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.name, error.message, error.code));
            }
        });

        // noinspection JSUnusedLocalSymbols
        this.stripePaymentRequest.on("cancel", () => {
            this.paymentModel.analyticsService.trackWalletPaymentRequestCancelled(this.walletProvider());
            this.rejectSubmission(new WalletPaymentSubmissionFailure(this.walletProvider() === WalletProvider.APPLE_PAY ? WalletPaymentErrorType.APPLE_PAY_CANCELLED : WalletPaymentErrorType.GOOGLE_PAY_CANCELLED));
        });
        return this;
    }

    /**
     * Stripe's canMakePayment() has no shallow "supported at all" variant, so a negative alone can't be
     * told apart from "not finished checking yet" - see the Safari-only check below for the one case where
     * we can tell the difference.
     */
    protected async determineWalletProvider(): Promise<WalletProvider | null | undefined> {
        // noinspection JSUnresolvedReference
        const availability: any = await this.stripePaymentRequest.canMakePayment();
        if (availability?.applePay) {
            return WalletProvider.APPLE_PAY;
        }
        if (availability?.googlePay) {
            return WalletProvider.GOOGLE_PAY;
        }

        // ApplePaySession is exposed by Safari only - its absence elsewhere means this check simply doesn't
        // apply, not that Apple Pay was ruled out, so that's treated the same as "not sure yet". Its
        // presence with canMakePayments() false is a genuine, race-free "no" though: unlike Stripe's
        // combined check above it isn't subject to the active-card discovery delay, and Google Pay isn't
        // available on Safari at all, so there's nothing else to wait on.
        // noinspection JSUnresolvedReference
        return typeof (<any>window).ApplePaySession === "undefined" || (<any>window).ApplePaySession.canMakePayments() ? null : undefined;
    }

    // noinspection JSUnusedLocalSymbols
    public async showPaymentSheet(amount: number): Promise<void> {
        try {
            this.stripePaymentRequest.update({
                total: {
                    label: "Order total" + (this.paymentModel.paymentPlatformPublicKey().startsWith("pk_test") ? "(Test)" : ""),
                    amount: Math.round(amount * 100),
                }
            });
        } catch (error) {
            await this.init();
            this.rejectSubmission && this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.name, error.message, error.code));
            return;
        }

        try {
            await this.stripePaymentRequest.show();
        } catch (error) {

            // We're currently only expecting a Google Pay error here. Apple Pay reports all errors via the stripe callback.
            this.rejectSubmission && this.rejectSubmission(new WalletPaymentSubmissionFailure(WalletPaymentErrorType.PROVIDER_ERROR, error.name, error.message, error.code));
        }
    }
}
